import React, { useCallback, useState } from "react";
import moment, { Moment } from "moment";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormMetaProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { IRelationOption, ITaskUIProps } from "../interface";
import {
	SalesActivityPriority,
	TaskType
} from "@/Contracts/Others/SalesActivity";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { ZomentumEntities } from "@/Contracts";
import FormBuilder from "antd-form-builder";
import Col from "antd/lib/col";
import { EColors } from "@atoms/ZColors/interface";
import { RadioChangeEvent } from "antd";
import { getNearestHalfHour, isVendorApp } from "@/Utils";
import "./index.less";
import "../index.less";

const ZAddTask: React.FC<ITaskUIProps> = ({
	onSubmit,
	onClose,
	form,
	isLoading = false,
	taskContact = null,
	showEmailThreadMessageDrawer,
	ZUserMultiSelectFormItem,
	updateTaskContact,
	ZOpportunitySelectFormItem,
	ZClientSelectFormItem,
	ZUserSelectFormItem,
	isSubmitDisabled = false,
	datePattern,
	handleFormChange,
	ZVendorPartnerSelectFormItem,
	ZVendorOpportunitySelectFormItem,
	ZVendorClientSelectFormItem,
	isCrossAssign,
	isReadOnly,
	isEditMode
}) => {
	const startDate = new Date(new Date().setHours(0, 0, 0, 0));
	const todayStartDateUnixTimeStamp = startDate.getTime();
	const [isCompletedDateError, setIsCompletedDateError] =
		useState<boolean>(false);

	const DateAndTime = () => (
		<FormBuilder
			form={form}
			disabled={isLoading}
			meta={{
				columns: 2,
				fields: [
					{
						key: "date",
						required: true,
						wrapperCol: { span: 15 },
						widget: EWidgets.DATEPICKER,
						widgetProps: {
							disabled: isReadOnly,
							datePattern,
							allowClear: false,
							className: "w-full",
							placeholder: "Due Date"
						},
						hasFeedback: true,
						initialValue: moment(startDate)
					},
					{
						key: "time",
						widget: EWidgets.TIMEPICKER,
						wrapperCol: { span: 13 },
						required: true,
						message: "Please enter time",
						widgetProps: {
							disabled: isReadOnly,
							minuteStep: 5,
							allowClear: false,
							className: "w-full",
							format: "HH:mm",
							placeholder: "Due Time"
						},
						hasFeedback: true,
						initialValue: getNearestHalfHour(new Date())
					}
				]
			}}
		/>
	);
	const isVendorApplication = isVendorApp();

	const getRelationOptions = useCallback(() => {
		const relationOptions: IRelationOption[] = [];

		if (isVendorApplication) {
			relationOptions.push(
				{
					label: "Opportunity",
					value: ZomentumEntities.VendorOpportunity,
					datatestid: "opportunity-radio",
					children: (
						<div className="mt-3 sales-activity-relation-options">
							<ZVendorOpportunitySelectFormItem
								disabled={isReadOnly}
							/>
						</div>
					)
				},
				{
					label: "Client",
					value: ZomentumEntities.VendorClient,
					datatestid: "opportunity-radio",
					children: (
						<div className="mt-3 sales-activity-relation-options">
							<ZVendorClientSelectFormItem
								disabled={isReadOnly}
							/>
						</div>
					)
				},
				{
					label: "Partner",
					value: ZomentumEntities.VendorCompany,
					children: (
						<div className="mt-3 sales-activity-relation-options">
							<ZVendorPartnerSelectFormItem
								disabled={isReadOnly}
							/>
						</div>
					)
				}
			);
		} else {
			relationOptions.push(
				{
					label: "Opportunity",
					value: ZomentumEntities.Opportunity,
					datatestid: "opportunity-radio",
					children: (
						<div className="mt-3 sales-activity-relation-options">
							<ZOpportunitySelectFormItem disabled={isReadOnly} />
						</div>
					)
				},
				{
					label: "Client",
					value: ZomentumEntities.Client,
					children: (
						<div className="mt-3 sales-activity-relation-options">
							<ZClientSelectFormItem disabled={isReadOnly} />
						</div>
					)
				}
			);
		}
		return relationOptions;
	}, [
		isVendorApplication,
		ZVendorClientSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem
	]);

	const sections: Array<IFormSectionProps> = [
		{
			isIndentFormItems: false,
			hideDivider: true,
			getMeta: () => {
				const fields: IFormFieldProps[] = [
					{
						key: "title",
						label: "Title",
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter title",
							disabled: isReadOnly
						},
						rules: [
							{
								whitespace: true,
								message: "Title cannot be empty"
							}
						],
						required: true,
						message: "Please enter title",
						hasFeedback: true
					},
					{
						key: "description",
						label: "Description",
						widget: EWidgets.INPUTTEXTAREA,
						widgetProps: {
							placeholder: "Please enter description",
							autoSize: { minRows: 3, maxRows: 6 },
							allowClear: true,
							disabled: isReadOnly
						}
					},
					{
						key: "custom_date",
						label: "Due Date",
						required: true,
						initialValue: moment(),
						widget: DateAndTime
					},
					{
						key: "task_type",
						label: "Type",
						widget: EWidgets.RADIOGROUP,
						widgetProps: {
							options: [
								{
									label: "To Do",
									value: TaskType.ToDo
								},
								{
									label: "Follow Up",
									value: TaskType.FollowUp
								},
								{
									label: "Call",
									value: TaskType.Call
								},
								{
									label: "Email",
									value: TaskType.Email
								}
							],
							disabled: isReadOnly
						},
						required: true,
						message: "Please select type",
						initialValue: TaskType.ToDo
					},
					{
						key: "priority",
						label: "Priority",
						widget: EWidgets.RADIOGROUP,
						widgetProps: {
							options: [
								{
									label: "High",
									value: SalesActivityPriority.High
								},
								{
									label: "Medium",
									value: SalesActivityPriority.Medium
								},
								{
									label: "Low",
									value: SalesActivityPriority.Low
								}
							],
							disabled: isReadOnly
						},
						required: true,
						message: "Please select priority",
						initialValue: SalesActivityPriority.Medium
					}
				];
				if (!isReadOnly) {
					fields.push(
						{
							key: "user_multi_select",
							render: () =>
								ZUserMultiSelectFormItem && (
									<ZUserMultiSelectFormItem
										disabled={isCrossAssign || isReadOnly}
									/>
								)
						},
						{
							key: "relation",
							label: "Related to",
							widget: EWidgets.RADIOGROUP,
							widgetProps: {
								onChange: (e: RadioChangeEvent) =>
									updateTaskContact &&
									updateTaskContact(e.target.value),
								className: "sales-activity-relation-radio",
								space: "middle",
								options: getRelationOptions(),
								disabled: isReadOnly
							},
							required: true,
							message: "Please select relation"
						},
						{
							key: "author_id",
							render: () => (
								<ZUserSelectFormItem
									showAssociatedUsers={
										isEditMode ? isReadOnly : true
									}
									disabled={isReadOnly}
								/>
							)
						}
					);
				}
				return {
					formItemLayout: {
						labelCol: { span: 5 },
						wrapperCol: { span: 19 }
					},
					fields
				};
			}
		},
		{
			isFooterSection: true,
			getMeta: () => {
				const meta: IFormMetaProps = {
					columns: 2,
					fields: [
						{
							key: "is_marked_completed",
							label: "Mark as done",
							labelCol: { span: 20 },
							wrapperCol: { span: 4 },
							widget: EWidgets.CHECKBOX,
							widgetProps: {
								disabled: isLoading || isReadOnly,
								className: "ml-1"
							}
						}
					]
				};
				if (form.getFieldValue("is_marked_completed")) {
					meta.fields.push({
						key: "completed_at",
						wrapperCol: { span: 24 },
						widget: EWidgets.DATEPICKER,
						hasFeedback: true,
						widgetProps: {
							datePattern,
							className: "w-full",
							disabled: isLoading || isReadOnly,
							placeholder: "Select date",
							disabledDate: (date: Moment) =>
								date
									? date.toDate().getTime() >=
									  todayStartDateUnixTimeStamp +
											24 * 60 * 60 * 1000
									: false
						},
						rules: [
							() => ({
								validator(_: any, value: any) {
									if (
										form.getFieldValue(
											"is_marked_completed"
										) &&
										!value
									) {
										setIsCompletedDateError(true);
										return Promise.reject(
											new Error("Enter completed date")
										);
									} else {
										setIsCompletedDateError(false);
										return Promise.resolve();
									}
								}
							})
						]
					});
				}
				return meta;
			}
		}
	];

	if (taskContact && (taskContact?.phone || taskContact?.email)) {
		sections[0].meta?.fields.splice(4, 0, {
			key: "contact",
			render: () => (
				<Col span={19} offset={5} className="flex items-center mb-6">
					{taskContact?.phone && taskContact?.phone?.length > 0 && (
						<a href={`tel:${taskContact?.phone}`} className="mr-6">
							<ZIcon
								name={Icons.PHONE}
								size="sm"
								color={EColors.BLUE}
								colorVariant={6}
								className="mr-3"
							/>
							{taskContact?.name?.full} on {taskContact?.phone}
						</a>
					)}
					{taskContact?.email && taskContact?.email?.length > 0 && (
						<span
							className="link"
							onClick={showEmailThreadMessageDrawer}
						>
							<ZIcon
								name={Icons.MAIL}
								size="sm"
								color={EColors.BLUE}
								colorVariant={6}
								className="mr-3"
							/>
							{taskContact?.name?.full} &lt;{taskContact?.email}
							&gt;
						</span>
					)}
				</Col>
			)
		});
	}

	const formProps: IFormProps = {
		key: "AddTaskForm",
		form,
		sections,
		disabled: isLoading || isReadOnly,
		onFinish: onSubmit,
		submitButtonText: "Save",
		isRightFooter: true,
		isStickFooter: true,
		isCancelAllowed: true,
		onCancel: onClose,
		isSubmitDisabled: isSubmitDisabled,
		isDynamic: true,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange,
		submitButtonProps: { className: isReadOnly ? "hidden" : undefined },
		cancelButtonProps: { className: isReadOnly ? "hidden" : undefined }
	};

	return (
		<ZForm
			{...formProps}
			className={`z-sales-activity-task-form py-4 overflow-y-hidden ${
				isCompletedDateError ? "with-footer-error" : ""
			}`}
		/>
	);
};

export default ZAddTask;
